import React from 'react';
import { Award, Target, Eye, Shield, Clock, Users2, Linkedin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { founders } from '../mock/mockData';
import usePageTitle from '../hooks/usePageTitle';

const About = () => {
  usePageTitle('About Us');
  const values = [
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'We ensure every product meets the highest industry standards with rigorous quality checks and certifications.'
    },
    {
      icon: Clock,
      title: 'Timely Delivery',
      description: 'Our efficient logistics network ensures your steel reaches your site exactly when you need it.'
    },
    {
      icon: Users2,
      title: 'Customer First',
      description: 'Your success is our priority. We provide personalized service and expert guidance at every step.'
    },
    {
      icon: Award,
      title: 'Industry Expertise',
      description: 'With 25+ years of experience, we understand the steel industry inside out.'
    }
  ];

  const milestones = [
    { year: '2007', event: 'Founded G.A ISPAT Pvt. Ltd.' },
    { year: '2010', event: 'Expanded product range to structural steel' },
    { year: '2013', event: 'Established partnerships with major mills' },
    { year: '2017', event: 'Crossed 300+ satisfied clients' },
    { year: '2020', event: 'Enhanced logistics and delivery network' },
    { year: '2024', event: 'Serving 500+ projects across Eastern India' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              About <span className="text-orange-600">G.A ISPAT</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Building India's infrastructure with premium quality TMT bars, round bars, and structural steel products since 2007
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1697698532634-ea59b636ccea?w=1200&q=80"
                alt="Steel Industry"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Our Story</h2>
              <p className="text-slate-700 leading-relaxed">
                Established in 2007, G.A ISPAT Pvt. Ltd. has grown to become a trusted name in the steel trading industry, specializing in TMT bars, round bars, and structural steel products. Our journey has been driven by an unwavering commitment to quality, reliability, and customer satisfaction.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Today, we serve construction companies, manufacturers, and industrial clients across India, supplying a comprehensive range of steel products including high-grade TMT bars, round bars for various applications, and structural steel for heavy construction projects.
              </p>
              <p className="text-slate-700 leading-relaxed">
                With a team of experienced professionals and a robust logistics network, we ensure that every order is fulfilled with precision and care. Our commitment to quality and timely delivery has made us a preferred choice for builders and contractors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Leadership</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Guided by experienced leaders with decades of industry expertise
            </p>
          </div>
          <div className="max-w-6xl mx-auto space-y-16">
            {founders.map((founder, index) => (
              <Card key={founder.id} className="border-2 border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className={`grid lg:grid-cols-5 gap-8 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={`lg:col-span-2 ${index % 2 === 1 ? 'lg:col-start-4' : ''}`}>
                    <div className="h-full">
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <CardContent className={`lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-slate-900 mb-2">{founder.name}</h3>
                      <p className="text-lg text-orange-600 font-semibold mb-4">{founder.designation}</p>
                      {founder.id === 2 && (
                        <div className="flex gap-3">
                          <a 
                            href="https://www.linkedin.com/in/vineet-garg" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-slate-100 hover:bg-blue-600 hover:text-white rounded-lg flex items-center justify-center transition-colors"
                            aria-label="LinkedIn Profile"
                          >
                            <Linkedin className="w-5 h-5" />
                          </a>
                        </div>
                      )}
                    </div>
                    <p className="text-slate-700 leading-relaxed text-justify">
                      {founder.description}
                    </p>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-2 border-slate-200 hover:border-orange-600 transition-colors">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-700 leading-relaxed">
                  To be the most reliable steel supplier in India by delivering superior quality TMT bars, round bars, and structural steel products with exceptional customer service and innovative solutions that help build a stronger nation.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-200 hover:border-orange-600 transition-colors">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-700 leading-relaxed">
                  To establish ourselves as India's leading steel trading company, recognized for our commitment to quality, sustainability, and contribution to the nation's infrastructure development.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The principles that guide our business and relationships with clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300 bg-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="w-8 h-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Journey</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Key milestones in our 25+ years of excellence
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-6 items-start group">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg group-hover:scale-110 transition-transform">
                      {milestone.year}
                    </div>
                  </div>
                  <div className="flex-1 pt-4">
                    <div className="bg-slate-50 rounded-lg p-6 group-hover:bg-orange-50 transition-colors">
                      <p className="text-lg text-slate-800 font-medium">{milestone.event}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Why Choose G.A ISPAT?</h2>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-500 mb-2">18+</div>
                <div className="text-slate-300">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-500 mb-2">500+</div>
                <div className="text-slate-300">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-orange-500 mb-2">50K+</div>
                <div className="text-slate-300">Tonnes Supplied</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
